'use client';

import { ChevronLeft } from 'lucide-react';

interface QuizProgressProps {
  currentStep: number;
  totalSteps: number;
  onBack?: () => void;
  showBackButton?: boolean;
}

// Get section label based on progress
function getSectionLabel(progress: number): string {
  if (progress < 30) return 'Your Situation';
  if (progress < 60) return 'What You Want';
  if (progress < 90) return 'Almost There';
  return 'Get Your Analysis';
}

// Get time estimate based on remaining steps
function getTimeEstimate(remainingSteps: number): string {
  const minutesPerStep = 0.4; // ~24 seconds per question
  const minutes = Math.ceil(remainingSteps * minutesPerStep);
  if (minutes <= 1) return 'Less than a minute left';
  return `~${minutes} min left`;
}

// Get encouraging message at certain milestones
function getEncouragement(progress: number): string | null {
  if (progress >= 48 && progress < 55) return "Halfway there - we're building your advantage";
  if (progress >= 73 && progress < 80) return "Almost done - your analysis is being prepared";
  return null;
}

export function QuizProgress({
  currentStep,
  totalSteps,
  onBack,
  showBackButton = true,
}: QuizProgressProps) {
  const progress = (currentStep / totalSteps) * 100;
  const remainingSteps = totalSteps - currentStep;
  const sectionLabel = getSectionLabel(progress);
  const timeEstimate = getTimeEstimate(remainingSteps);
  const encouragement = getEncouragement(progress);

  return (
    <div className="w-full max-w-2xl mx-auto px-4 py-4">
      <div className="flex items-center justify-between mb-2">
        {showBackButton && currentStep > 1 ? (
          <button
            onClick={onBack}
            className="flex items-center gap-1 text-gray-400 hover:text-gray-300 transition-colors"
            aria-label="Go back"
          >
            <ChevronLeft size={20} />
            <span className="text-sm">Back</span>
          </button>
        ) : (
          <div className="w-16"></div>
        )}

        <div className="text-center">
          <div className="text-xs text-primary font-medium mb-0.5">{sectionLabel}</div>
          <div className="text-xs text-gray-500">{timeEstimate}</div>
        </div>

        <div className="text-sm text-gray-400 w-16 text-right">
          <span className="text-white font-medium">{currentStep}</span>/{totalSteps}
        </div>
      </div>

      <div className="w-full bg-gray-800 rounded-full h-2 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-300 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      {encouragement && (
        <div className="text-center mt-2">
          <span className="text-xs text-primary/80">{encouragement}</span>
        </div>
      )}
    </div>
  );
}
